import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { RefreshCw, TrendingUp, Users, Package, IndianRupee, Percent, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatCurrency, formatPercentage, formatQuantity } from '../utils/formatters';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, ComposedChart, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generate FY options (last 5 years)
const generateFYOptions = () => {
  const now = new Date();
  const currentYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let i = 0; i < 5; i++) {
    const startYear = currentYear - i;
    const endYear = (startYear + 1) % 100;
    options.push(`${startYear}-${endYear.toString().padStart(2, '0')}`);
  }
  return options;
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg">
        <p className="font-medium text-sm mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes('%') ? `${entry.value.toFixed(1)}%` : formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Summary card component
const SummaryCard = ({ title, value, icon: Icon, subtitle, trend }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${trend === 'up' ? 'bg-green-100' : trend === 'down' ? 'bg-red-100' : 'bg-slate-100'}`}>
          <Icon className={`h-5 w-5 ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-slate-600'}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// GP Slab colors
const SLAB_COLORS = {
  'Upto 20%': '#ef4444',
  '20 to 30%': '#f97316',
  '30 to 40%': '#eab308',
  '40 to 50%': '#84cc16',
  '50 to 60%': '#22c55e',
  '60 to 70%': '#14b8a6',
  '70 to 80%': '#3b82f6',
  '80% & Above': '#8b5cf6'
};

export default function GPAnalyticsPage() {
  const [selectedFY, setSelectedFY] = useState(generateFYOptions()[0]);
  const [activeTab, setActiveTab] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [productData, setProductData] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchMonthlyData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/reports/gp/monthly?fy=${selectedFY}`, { headers: getAuthHeader() });
      setMonthlyData(res.data);
    } catch (err) {
      console.error('Error fetching monthly GP data:', err);
    }
  }, [selectedFY]);

  const fetchClientData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/reports/gp/by-client?fy=${selectedFY}&limit=20`, { headers: getAuthHeader() });
      setClientData(res.data);
    } catch (err) {
      console.error('Error fetching client GP data:', err);
    }
  }, [selectedFY]);

  const fetchProductData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/reports/gp/by-product?fy=${selectedFY}`, { headers: getAuthHeader() });
      setProductData(res.data);
    } catch (err) {
      console.error('Error fetching product GP data:', err);
    }
  }, [selectedFY]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchMonthlyData(), fetchClientData(), fetchProductData()]);
    setLoading(false);
  }, [fetchMonthlyData, fetchClientData, fetchProductData]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Prepare chart data for monthly view
  const monthlyChartData = monthlyData?.monthly?.map(m => ({
    month: m.month.split('. ')[1] || m.month,
    'Nett Sales': m.nett_sales,
    'GP': m.gp,
    'GP%': m.gp_percent
  })) || [];

  // Prepare chart data for clients (top 10)
  const clientChartData = clientData?.by_gp_amount?.slice(0, 10).map(c => ({
    name: c.customer_name?.length > 15 ? c.customer_name.substring(0, 15) + '...' : c.customer_name,
    'GP': c.gp,
    'GP%': c.gp_percent
  })) || [];

  // Prepare chart data for products (top 10)
  const productChartData = productData?.by_gp_amount?.slice(0, 10).map(p => ({
    name: p.classification?.length > 20 ? p.classification.substring(0, 20) + '...' : p.classification,
    'GP': p.gp,
    'GP%': p.gp_percent
  })) || [];

  return (
    <DashboardLayout>
    <div className="p-6 space-y-6" data-testid="gp-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">GP Intelligence Dashboard</h1>
          <p className="text-muted-foreground">Gross Profit Analysis by Month, Client, and Product</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedFY} onValueChange={setSelectedFY}>
            <SelectTrigger className="w-32" data-testid="fy-selector">
              <SelectValue placeholder="Select FY" />
            </SelectTrigger>
            <SelectContent>
              {generateFYOptions().map(fy => (
                <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAllData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Total Nett Sales"
          value={formatCurrency(monthlyData?.grand_total?.nett_sales || 0)}
          icon={IndianRupee}
          subtitle={`FY ${selectedFY}`}
        />
        <SummaryCard
          title="Total Gross Profit"
          value={formatCurrency(monthlyData?.grand_total?.gp || 0)}
          icon={TrendingUp}
          trend="up"
        />
        <SummaryCard
          title="Overall GP%"
          value={formatPercentage(monthlyData?.grand_total?.gp_percent || 0)}
          icon={Percent}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="monthly" data-testid="tab-monthly">
            <BarChart3 className="h-4 w-4 mr-2" />
            Monthly GP
          </TabsTrigger>
          <TabsTrigger value="client" data-testid="tab-client">
            <Users className="h-4 w-4 mr-2" />
            By Client
          </TabsTrigger>
          <TabsTrigger value="product" data-testid="tab-product">
            <Package className="h-4 w-4 mr-2" />
            By Product
          </TabsTrigger>
        </TabsList>

        {/* Monthly GP Tab */}
        <TabsContent value="monthly" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Monthly Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Monthly Sales vs GP</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="Nett Sales" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="left" dataKey="GP" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="GP%" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b' }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* GP Slab Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">GP Slab Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slab</TableHead>
                      <TableHead className="text-right">Sales</TableHead>
                      <TableHead className="text-right">GP%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyData?.slab_distribution?.map((slab, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: SLAB_COLORS[slab.slab] }} />
                            <span className="text-sm">{slab.slab}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(slab.sales)}</TableCell>
                        <TableCell className="text-right text-sm">{formatPercentage(slab.gp_percent)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Nett Sales</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                    <TableHead className="text-right">GP</TableHead>
                    <TableHead className="text-right">GP%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyData?.monthly?.map((m, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{m.month}</TableCell>
                      <TableCell className="text-right">{formatCurrency(m.nett_sales)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(m.total_cost)}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">{formatCurrency(m.gp)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={m.gp_percent >= 50 ? 'default' : m.gp_percent >= 30 ? 'secondary' : 'destructive'}>
                          {formatPercentage(m.gp_percent)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Grand Total Row */}
                  {monthlyData?.grand_total && (
                    <TableRow className="bg-slate-50 font-bold">
                      <TableCell>Grand Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(monthlyData.grand_total.nett_sales)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(monthlyData.grand_total.total_cost)}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(monthlyData.grand_total.gp)}</TableCell>
                      <TableCell className="text-right">
                        <Badge>{formatPercentage(monthlyData.grand_total.gp_percent)}</Badge>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Client Tab */}
        <TabsContent value="client" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Client Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Top 10 Clients by GP</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={clientChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="GP" fill="#22c55e" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Clients by GP% */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Clients by GP%</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {clientData?.by_gp_percent?.slice(0, 15).map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="text-sm truncate flex-1 mr-2">{c.customer_name}</span>
                      <Badge variant={c.gp_percent >= 50 ? 'default' : 'secondary'}>
                        {formatPercentage(c.gp_percent)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Client Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Client GP Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Nett Sales</TableHead>
                    <TableHead className="text-right">GP</TableHead>
                    <TableHead className="text-right">% of Total GP</TableHead>
                    <TableHead className="text-right">Avg GP%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientData?.by_gp_amount?.map((c, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{c.customer_name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(c.nett_sales)}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">{formatCurrency(c.gp)}</TableCell>
                      <TableCell className="text-right">{formatPercentage(c.gp_contribution)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={c.gp_percent >= 50 ? 'default' : c.gp_percent >= 30 ? 'secondary' : 'destructive'}>
                          {formatPercentage(c.gp_percent)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Product Tab */}
        <TabsContent value="product" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Product Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Top 10 Classifications by GP</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={productChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={150} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="GP" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Classifications by GP% */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Classifications by GP%</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {productData?.by_gp_percent?.slice(0, 15).map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="text-sm truncate flex-1 mr-2">{p.classification}</span>
                      <Badge variant={p.gp_percent >= 50 ? 'default' : 'secondary'}>
                        {formatPercentage(p.gp_percent)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Product Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Product Classification GP Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Classification</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Nett Sales</TableHead>
                    <TableHead className="text-right">GP</TableHead>
                    <TableHead className="text-right">GP%</TableHead>
                    <TableHead className="text-right">Avg Unit Cost</TableHead>
                    <TableHead className="text-right">Avg Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productData?.by_gp_amount?.map((p, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{p.classification}</TableCell>
                      <TableCell className="text-right">{formatQuantity(p.quantity)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(p.nett_sales)}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">{formatCurrency(p.gp)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={p.gp_percent >= 50 ? 'default' : p.gp_percent >= 30 ? 'secondary' : 'destructive'}>
                          {formatPercentage(p.gp_percent)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(p.avg_unit_cost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(p.avg_price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </DashboardLayout>
  );
}
